import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import axios from "axios";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

// ==========================
// SEND OTP + CHECK USER EXISTS
// ==========================
export const publicSendOtp =
  createAsyncThunk(
    "publicAuth/sendOtp",
    async (
      phone_number: string,
      { rejectWithValue }
    ) => {
      try {
        const formData =
          new FormData();

        formData.append(
          "phone_number",
          phone_number
        );

        const res =
          await axios.post(
            `${API_URL}/api/auth/user-login/verify-mobile`,
            formData
          );

        localStorage.setItem(
          "public_mobile",
          phone_number
        );

        if (res.data?.otp) {
          localStorage.setItem(
            "server_otp",
            String(
              res.data.otp
            )
          );
        }

        localStorage.setItem(
          "login_response",
          JSON.stringify(
            res.data
          )
        );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data?.message ||
            "Failed to send OTP"
        );
      }
    }
  );

// ==========================
// VERIFY OTP IN FRONTEND
// ==========================
export const publicVerifyOtp =
  createAsyncThunk(
    "publicAuth/verifyOtp",
    async (
      payload: {
        phone_number: string;
        otp: string;
      },
      { rejectWithValue }
    ) => {
      try {
        const serverOtp =
          localStorage.getItem(
            "server_otp"
          );

        if (!serverOtp) {
          return rejectWithValue(
            "OTP expired. Please resend OTP"
          );
        }

        if (
          String(payload.otp) !==
          String(serverOtp)
        ) {
          return rejectWithValue(
            "Invalid OTP"
          );
        }

        localStorage.setItem(
          "public_mobile",
          payload.phone_number
        );

        let savedResponse: any =
          {};

        try {
          savedResponse =
            JSON.parse(
              localStorage.getItem(
                "login_response"
              ) || "{}"
            );
        } catch {
          savedResponse = {};
        }

        return {
          ...savedResponse,
          success: true,
          message:
            "OTP verified successfully",
        };
      } catch {
        return rejectWithValue(
          "OTP verification failed"
        );
      }
    }
  );

// ==========================
// REGISTER USER / BASIC INFO
// ==========================
export const publicRegister =
  createAsyncThunk(
    "publicAuth/register",
    async (
      payload: {
        phone_number: string;
        user_fullname: string;
        email: string;
        dob: string;
      },
      { rejectWithValue }
    ) => {
      try {
        const formData =
          new FormData();

        formData.append(
          "phone_number",
          payload.phone_number
        );

        formData.append(
          "user_fullname",
          payload.user_fullname
        );

        formData.append(
          "full_name",
          payload.user_fullname
        );

        formData.append(
          "email",
          payload.email
        );

        formData.append(
          "dob",
          payload.dob
        );

        const res =
          await axios.post(
            `${API_URL}/api/auth/user-register`,
            formData
          );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data?.message ||
            "Registration failed"
        );
      }
    }
  );

// ==========================
// GET ELIGIBLE CLUBS
// ==========================
export const fetchEligibleClubs =
  createAsyncThunk(
    "publicAuth/eligibleClubs",
    async (
      user_id: number,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${API_URL}/jm/eligible-clubs/${user_id}`
          );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data?.message ||
            "No contacts found"
        );
      }
    }
  );

// ==========================
// JOIN CLUB
// ==========================
export const publicJoinClub =
  createAsyncThunk(
    "publicAuth/joinClub",
    async (
      payload: {
        user_id: number;
        client_id: number;
      },
      { rejectWithValue }
    ) => {
      try {
        const formData =
          new FormData();

        formData.append(
          "user_id",
          String(
            payload.user_id
          )
        );

        formData.append(
          "client_id",
          String(
            payload.client_id
          )
        );

        const res =
          await axios.post(
            `${API_URL}/jm/join-club`,
            formData
          );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data?.message ||
            "Failed to join club"
        );
      }
    }
  );

interface PublicAuthState {
  token: string | null;
  user: any | null;
  contact: any | null;
  clubs: any[];

  loading: boolean;

  otpSent: boolean;
  otpVerified: boolean;

  userExists: boolean;

  needRegistration: boolean;
  needJoinClub: boolean;

  error: string | null;
}

const safeParse = (
  key: string,
  fallback: any
) => {
  try {
    return JSON.parse(
      localStorage.getItem(
        key
      ) || JSON.stringify(
        fallback
      )
    );
  } catch {
    return fallback;
  }
};

const initialState: PublicAuthState =
  {
    token:
      localStorage.getItem(
        "token"
      ) || null,

    user: safeParse(
      "user",
      null
    ),

    contact: safeParse(
      "contact",
      null
    ),

    clubs: safeParse(
      "clubs",
      []
    ),

    loading: false,

    otpSent: false,
    otpVerified: false,

    userExists: false,

    needRegistration:
      false,

    needJoinClub: false,

    error: null,
  };

const publicAuthSlice =
  createSlice({
    name: "publicAuth",

    initialState,

    reducers: {
      logoutPublicUser: (
        state
      ) => {
        state.token = null;
        state.user = null;
        state.contact = null;
        state.clubs = [];

        state.otpSent = false;
        state.otpVerified =
          false;

        state.userExists =
          false;

        state.needRegistration =
          false;

        state.needJoinClub =
          false;

        state.error = null;

        localStorage.removeItem(
          "token"
        );

        localStorage.removeItem(
          "user"
        );

        localStorage.removeItem(
          "contact"
        );

        localStorage.removeItem(
          "clubs"
        );

        localStorage.removeItem(
          "public_mobile"
        );

        localStorage.removeItem(
          "server_otp"
        );

        localStorage.removeItem(
          "login_response"
        );
      },

      resetPublicAuthError: (
        state
      ) => {
        state.error = null;
      },

      resetPublicAuthFlow: (
        state
      ) => {
        state.otpSent = false;
        state.otpVerified =
          false;

        state.userExists =
          false;

        state.needRegistration =
          false;

        state.needJoinClub =
          false;

        state.error = null;

        localStorage.removeItem(
          "server_otp"
        );

        localStorage.removeItem(
          "login_response"
        );
      },
    },

    extraReducers: (
      builder
    ) => {
      builder
        .addCase(
          publicSendOtp.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          publicSendOtp.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;
            state.otpSent = true;

            const data =
              action.payload;

            state.userExists =
              !!data?.user_exists;

            if (
              data?.user_exists
            ) {
              state.token =
                data?.token ||
                null;

              state.user =
                data?.user ||
                null;

              state.needRegistration =
                false;

              state.needJoinClub =
                false;

              if (
                data?.token
              ) {
                localStorage.setItem(
                  "token",
                  data.token
                );
              }

              if (
                data?.user
              ) {
                localStorage.setItem(
                  "user",
                  JSON.stringify(
                    data.user
                  )
                );
              }
            } else {
              state.token = null;
              state.user = null;

              state.needRegistration =
                false;

              state.needJoinClub =
                false;
            }
          }
        )

        .addCase(
          publicSendOtp.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;
            state.otpSent = false;

            state.error =
              action.payload as string;
          }
        )

        .addCase(
          publicVerifyOtp.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          publicVerifyOtp.fulfilled,
          (state) => {
            state.loading = false;
            state.otpVerified =
              true;

            state.needRegistration =
              true;

            state.needJoinClub =
              false;

            state.error = null;
          }
        )

        .addCase(
          publicVerifyOtp.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;
            state.otpVerified =
              false;

            state.error =
              action.payload as string;
          }
        )

        .addCase(
          publicRegister.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          publicRegister.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            const data =
              action.payload?.data ||
              action.payload;

            state.token =
              data?.token ||
              null;

            state.user =
              data?.user ||
              null;

            state.contact =
              data?.contact ||
              null;

            state.clubs =
              data?.clubs ||
              [];

            state.needRegistration =
              false;

            state.needJoinClub =
              true;

            if (
              data?.token
            ) {
              localStorage.setItem(
                "token",
                data.token
              );
            }

            if (
              data?.user
            ) {
              localStorage.setItem(
                "user",
                JSON.stringify(
                  data.user
                )
              );
            }

            if (
              data?.contact
            ) {
              localStorage.setItem(
                "contact",
                JSON.stringify(
                  data.contact
                )
              );
            }

            if (
              data?.clubs
            ) {
              localStorage.setItem(
                "clubs",
                JSON.stringify(
                  data.clubs
                )
              );
            }

            localStorage.removeItem(
              "server_otp"
            );

            localStorage.removeItem(
              "login_response"
            );
          }
        )

        .addCase(
          publicRegister.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.error =
              action.payload as string;
          }
        )

        .addCase(
          fetchEligibleClubs.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchEligibleClubs.fulfilled,
          (
            state,
            action
          ) => {
            state.loading = false;

            const clubs =
              action.payload?.data ||
              [];

            state.clubs = clubs;

            state.needJoinClub =
              clubs.length > 0;

            localStorage.setItem(
              "clubs",
              JSON.stringify(
                clubs
              )
            );
          }
        )

        .addCase(
          fetchEligibleClubs.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;
            state.clubs = [];

            state.needJoinClub =
              false;

            state.error =
              action.payload as string;
          }
        )

        .addCase(
          publicJoinClub.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          publicJoinClub.fulfilled,
          (state) => {
            state.loading = false;

            state.needJoinClub =
              false;
          }
        )

        .addCase(
          publicJoinClub.rejected,
          (
            state,
            action
          ) => {
            state.loading = false;

            state.error =
              action.payload as string;
          }
        );
    },
  });

export const {
  logoutPublicUser,
  resetPublicAuthError,
  resetPublicAuthFlow,
} = publicAuthSlice.actions;

export default publicAuthSlice.reducer;
